import { cloneElement, Children, ReactElement, useLayoutEffect, forwardRef } from 'react'
import { PressResponder, useKeyboard } from '@react-aria/interactions'
import { useMenuTrigger } from '@react-aria/menu'
import Foco from 'react-foco'
import { useMenuTriggerState } from '@react-stately/menu'
import { MenuTriggerProps } from './Types'
import { Popover } from 'components/Popover/Popover'
import { mergeProps, useObjectRef } from '@react-aria/utils'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { autoUpdate } from '@floating-ui/dom'
import { useFloating, flip, offset, inline, shift, size } from '@floating-ui/react-dom'
import { FloatingPortal } from '@floating-ui/react-dom-interactions'

const MenuTrigger = forwardRef<HTMLElement, MenuTriggerProps>(
  (
    {
      children,
      showOverlay = true,
      placement = 'bottom-start',
      popoverClassName,
      popoverTargetRef,
      __UNSAFE_popoverStyle = {},
      containFocus = true,
      ...props
    },
    fRef,
  ) => {
    const [trigger, menu] = Children.toArray(children)

    const ref = useObjectRef<HTMLElement>(fRef)
    const {
      x,
      y,
      reference: triggerRef,
      floating,
      refs,
    } = useFloating({
      placement: placement,
      strategy: 'fixed',
      middleware: [
        offset(4),
        inline(),
        flip(),
        size({
          padding: 16,
          apply({ availableHeight, elements }) {
            Object.assign(elements.floating.style, {
              visibility: 'visible',
              maxHeight: `${Math.max(96, availableHeight)}px`,
            })
          },
        }),
        shift({ padding: 4 }),
      ],
      whileElementsMounted: (reference, floating, update) => {
        return autoUpdate(reference, floating, update, {
          animationFrame: true,
        })
      },
    })

    useLayoutEffect(() => {
      if (ref.current) {
        triggerRef(ref.current)
      }
    }, [ref, triggerRef])

    const state = useMenuTriggerState({
      ...props,
      onOpenChange: (isOpen) => {
        refs.setFloating(null)
        props.onOpenChange?.(isOpen)
      },
    })

    useLayoutEffect(() => {
      requestAnimationFrame(() => {
        if (popoverTargetRef?.current) {
          triggerRef(popoverTargetRef.current)
        } else if (ref.current) {
          triggerRef(ref.current)
        }
      })
    }, [ref, triggerRef, popoverTargetRef, state.isOpen])

    const { menuTriggerProps, menuProps } = useMenuTrigger({ type: 'menu' }, state, ref)
    const { keyboardProps } = useKeyboard(props)
    const { isMobile } = useResponsiveDesign()

    const popover = (
      <Popover
        data-theme={props['data-theme']}
        style={{
          position: 'fixed',
          top: y || 0,
          left: x || 0,
          visibility: 'hidden',
          ...__UNSAFE_popoverStyle,
        }}
        className={popoverClassName}
        onClose={state.close}
        isOpen={state.isOpen}
        ref={floating}
        containFocus={containFocus}
        isDismissable
      >
        {cloneElement(menu as ReactElement, { ...menuProps, autoFocus: 'first', onClose: state.close })}
      </Popover>
    )

    return (
      <>
        <PressResponder {...menuTriggerProps} preventFocusOnPress={isMobile} ref={ref} isPressed={state.isOpen}>
          {cloneElement(trigger as ReactElement, mergeProps((trigger as ReactElement).props, keyboardProps))}
        </PressResponder>
        {state.isOpen && (
          <>
            {showOverlay ? (
              <FloatingPortal id="floating-ui-root">{popover}</FloatingPortal>
            ) : (
              <Foco onClickOutside={state.close}>{popover}</Foco>
            )}
          </>
        )}
      </>
    )
  },
)

export { MenuTrigger }
